import { useEffect, useRef } from "react";
import { app, appPerformanceMetrics, HostMemoryMetrics, ResumeContext } from "@microsoft/teams-js";

// O host termina a aplicação se o TeamsJS não enviar readyToUnload em 30s, e esse sinal só sai
// depois que a promise devolvida pelo nosso handler resolve. O POST de visibilidade usa keepalive
// (a entrega sobrevive à destruição do documento), então não há motivo para esperar a resposta:
// um teto curto troca "sinal confirmado" por "não ser terminado por engano".
const SUSPEND_DRAIN_TIMEOUT_MS = 1500;

// Mede se o JS continuou rodando durante a suspensão e se os timers foram estrangulados. Não loga
// enquanto suspenso — só acumula e despeja uma linha no resume: a doc pede para a aplicação não
// fazer requisições nesse estado, e um flush de log poderia acionar o SSO com o host sem responder.
const HEARTBEAT_INTERVAL_MS = 5000;
const HEARTBEAT_MAX_SAMPLES = 360; // 30min de suspensão (o teto de cache do host) a cada 5s

// O heartbeat de memória do host é periódico e o teto do batch de log é em bytes, disputado com o
// trace SIP. Só registramos quando o estado muda ou quando o total se move o suficiente para ser
// informativo — o bastante para ver a curva crescer troca a troca sem afogar o arquivo.
const MEMORY_LOG_DELTA_KB = 5 * 1024;

export interface TeamsAppSuspensionCallbacks {
  /** Chamado antes da suspensão ou da terminação. O hook impõe SUSPEND_DRAIN_TIMEOUT_MS. */
  onBeforeSuspendOrTerminate?: () => void | Promise<void>;
  /**
   * Chamado quando a aplicação volta de uma suspensão.
   *
   * Deliberadamente SEM argumentos: o ResumeContext não sai deste módulo. Navegar para o
   * `contentUrl` que o host oferece recarregaria o documento e mataria a chamada em curso, que é
   * exatamente o que a suspensão existe para preservar — então o tipo não dá ao chamador como
   * fazer isso por engano.
   */
  onResume?: () => void;
  /**
   * Rearma os handlers do host que a suspensão apaga (ex.: registerOnThemeChangeHandler).
   * "When an app is suspended, all the registered handlers are deleted."
   */
  reregisterHostHandlers?: () => void;
}

/**
 * Opta a aplicação no App Suspension do Teams: com os dois handlers de ciclo de vida registrados,
 * o host **esconde** o iframe ao trocar de app em vez de destruí-lo — preservando o realm do
 * softphone e, com ele, uma chamada em andamento.
 *
 * Regra que sustenta tudo: **nada no caminho de resume pode escrever em `softphoneConfig`,
 * `registrationStatus`, `locale` ou `teamsSsoState`.** Esses estados trocam a branch renderizada
 * por App.tsx ou a `src` do iframe, e qualquer um dos dois recarrega o softphone — derrubando a
 * chamada mesmo tendo sido suspensa com sucesso.
 *
 * Suspensão não é garantida: o host pode terminar a aplicação por pressão de memória, por exceder
 * o número máximo de apps suspensos, ou por não suportar o recurso na plataforma (Android não
 * suporta). Nesses casos o comportamento degrada para o de sempre — documento recriado no retorno.
 *
 * https://learn.microsoft.com/microsoftteams/platform/tabs/how-to/app-caching
 */
export function useTeamsAppSuspension(callbacks: TeamsAppSuspensionCallbacks): void {
  // As callbacks trocam de identidade a cada render (sendAppVisibility depende do payload de
  // check-in), mas o registro no host tem que acontecer uma única vez e cedo. O latch resolve os
  // dois: efeito com deps [], closures sempre lendo a versão atual. Sem ele, o handler ficaria
  // preso na primeira versão de sendAppVisibility — a que retorna cedo por não haver identidade
  // ainda — e o sinal nunca sairia.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    const cycleRef = { current: 0 };
    const suspendedAtRef: { current: number | null } = { current: null };
    const heartbeatGaps: number[] = [];
    let heartbeatTimer: number | null = null;
    let heartbeatLastTick = 0;

    let lastMemoryState: string | null = null;
    let lastMemoryTotalKB = 0;

    /**
     * Heartbeat de memória do host (`@beta`). Vale por dois motivos: `appState` diz **do host** se
     * estamos suspensos, sem inferir pelos nossos handlers; e `frameMemoryMetrics` dá a nossa fatia
     * por frame, que é o que decide quando somos despejados — o despejo é por orçamento de memória
     * compartilhado com os apps nativos do Teams, e a única alavanca que temos é o nosso footprint.
     * Também é a única forma de medir isso no celular, onde não há DevTools.
     */
    function handleMemoryMetrics(metrics: HostMemoryMetrics): void {
      const frames = metrics.frameMemoryMetrics ?? [];
      const totalKB = frames.reduce((sum, frame) => sum + (frame.workingSetSizeKB || 0), 0);

      const stateChanged = metrics.appState !== lastMemoryState;
      const movedEnough = Math.abs(totalKB - lastMemoryTotalKB) >= MEMORY_LOG_DELTA_KB;
      if (!stateChanged && !movedEnough) return;

      lastMemoryState = metrics.appState;
      lastMemoryTotalKB = totalKB;

      const detail = frames
        .map((frame) => `${frame.hostAndPath}=${frame.workingSetSizeKB}/${frame.commitSizeKB}KB`)
        .join(" ");
      console.log(`[memory] appState=${metrics.appState} totalWorkingSet=${totalKB}KB ${detail}`);
    }

    function registerMemoryMetricsHandler() {
      try {
        if (!appPerformanceMetrics.isSupported()) return false;
        appPerformanceMetrics.registerHostMemoryMetricsHandler(handleMemoryMetrics);
        return true;
      } catch {
        // Capability ausente neste host; segue sem a medição.
        return false;
      }
    }

    function startHeartbeat() {
      // Sem isto, um segundo beforeSuspendOrTerminate sem resume entre eles (o host o chama mesmo
      // quando não suspende) deixaria o intervalo anterior órfão para sempre, empilhando no mesmo
      // array de gaps e corrompendo a medição.
      stopHeartbeat();
      heartbeatGaps.length = 0;
      heartbeatLastTick = Date.now();
      heartbeatTimer = window.setInterval(() => {
        const now = Date.now();
        heartbeatGaps.push(now - heartbeatLastTick);
        heartbeatLastTick = now;

        // O host chama beforeSuspendOrTerminate mesmo quando não suspende (troca entre abas da
        // mesma aplicação, por exemplo), e nesse caso nenhum resume vem parar o timer. Encerrar
        // no teto evita um intervalo vivo para sempre.
        if (heartbeatGaps.length >= HEARTBEAT_MAX_SAMPLES) {
          stopHeartbeat();
        }
      }, HEARTBEAT_INTERVAL_MS);
    }

    function stopHeartbeat() {
      if (heartbeatTimer !== null) {
        window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    }

    async function handleBeforeSuspendOrTerminate(): Promise<void> {
      const cycle = ++cycleRef.current;
      const startedAt = Date.now();
      suspendedAtRef.current = startedAt;

      // Contagem de iframes e visibilityState distinguem, no arquivo de log, "o host escondeu o
      // frame" de "o host destruiu o documento".
      console.log(
        `[lifecycle] beforeSuspendOrTerminate #${cycle} visibility=${document.visibilityState} iframes=${document.querySelectorAll("iframe").length}`
      );
      startHeartbeat();

      try {
        // Nada de liberar recursos aqui, ao contrário do que a doc sugere: o que ela pede para
        // soltar (listeners, sync, requisições) é justamente a chamada que queremos preservar.
        await Promise.race([
          Promise.resolve(callbacksRef.current.onBeforeSuspendOrTerminate?.()),
          new Promise<void>((resolve) => window.setTimeout(resolve, SUSPEND_DRAIN_TIMEOUT_MS)),
        ]);
      } catch (error) {
        console.warn("[lifecycle] callback de beforeSuspendOrTerminate falhou:", error);
      }

      // Perto de 30s aqui significa que o host terminou a aplicação por conta própria — o que
      // apareceria no log como uma suspensão que nunca teve resume.
      console.log(`[lifecycle] beforeSuspendOrTerminate #${cycle} resolvido em ${Date.now() - startedAt}ms`);
    }

    function handleResume(context: ResumeContext): void {
      const cycle = cycleRef.current;
      const suspendedMs = suspendedAtRef.current === null ? -1 : Date.now() - suspendedAtRef.current;
      stopHeartbeat();

      console.log(
        `[lifecycle] onResume #${cycle} suspenso=${suspendedMs}ms entityId=${context.entityId} contentUrl=${String(context.contentUrl)} (navegação IGNORADA) visibility=${document.visibilityState} iframes=${document.querySelectorAll("iframe").length}`
      );
      if (heartbeatGaps.length > 0) {
        // Um salto muito acima de HEARTBEAT_INTERVAL_MS no meio da lista indica timers
        // estrangulados durante a suspensão — provável perda de keepalive/registro SIP.
        console.log(`[lifecycle] heartbeat gaps(ms)=[${heartbeatGaps.join(",")}]`);
      }

      // A suspensão apaga os handlers registrados, o próprio par de ciclo de vida incluído. Sem
      // este rearme, a segunda troca de app não seria suspensa (e poderia nem chamar o
      // beforeSuspendOrTerminate, virando terminação silenciosa).
      registerLifecycleHandlers();

      try {
        callbacksRef.current.reregisterHostHandlers?.();
      } catch (error) {
        console.warn("[lifecycle] rearme de handlers do host falhou:", error);
      }

      try {
        callbacksRef.current.onResume?.();
      } catch (error) {
        console.warn("[lifecycle] callback de onResume falhou:", error);
      }

      // A doc manda sinalizar que a inicialização terminou. Hoje o host não gate a tela nisso
      // (nenhum dos manifests declara showLoadingIndicator), mas a rejeição precisa ser tratada:
      // uma unhandled rejection é capturada pelo clientLog e vira mais uma linha de log.
      void app
        .notifySuccess()
        .then(() => console.log("[lifecycle] notifySuccess enviado"))
        .catch((error) => console.warn("[lifecycle] notifySuccess falhou:", error));
    }

    function registerLifecycleHandlers() {
      app.lifecycle.registerBeforeSuspendOrTerminateHandler(handleBeforeSuspendOrTerminate);
      app.lifecycle.registerOnResumeHandler(handleResume);
      // O heartbeat de memória é mais um handler que a suspensão apaga, então entra no mesmo
      // rearme. O estado guardado é zerado para a primeira métrica depois do resume ser registrada
      // sempre, e não filtrada pelo delta contra um valor de antes da suspensão.
      lastMemoryState = null;
      lastMemoryTotalKB = 0;
      registerMemoryMetricsHandler();
    }

    async function registerWithHost() {
      try {
        await app.initialize();

        // Antes do guard de suspensão: a medição de memória é útil mesmo num host que não suspende
        // — é ela que diz quanto a aplicação ocupa, e no celular é a única forma de saber.
        const memorySupported = registerMemoryMetricsHandler();

        // O par é @beta no SDK e o suporte varia por cliente; sem ele a aplicação segue com o
        // comportamento de sempre (documento recriado a cada abertura).
        if (typeof app.lifecycle?.registerOnResumeHandler !== "function") {
          console.warn("[lifecycle] app.lifecycle indisponível neste host; suspensão não solicitada");
          return;
        }

        registerLifecycleHandlers();
        console.log(
          `[lifecycle] handlers registrados (beforeSuspendOrTerminate + onResume) memoryMetrics=${memorySupported}`
        );
      } catch (error) {
        // Fora do cliente Teams o initialize falha; nada a fazer além de seguir sem suspensão.
        console.warn("[lifecycle] registro dos handlers falhou:", error);
      }
    }

    // O registro tem que acontecer cedo e sem depender de check-in ou SSO: "If the Teams client
    // doesn't see these registrations before the user leaves the app, the app isn't cached."
    void registerWithHost();

    // Sem desregistro: o TeamsJS não expõe um, e este efeito vive enquanto a aplicação existir.
    return stopHeartbeat;
  }, []);
}
